import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useSnackbar } from 'notistack'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { DataGrid, GridColDef, GridToolbarContainer, GridToolbarFilterButton } from '@mui/x-data-grid'
import api from 'balkerne-core/api'
import { PortfolioType, PortfolioTypeIcon } from 'balkerne-core/roles'
import capitalise from 'balkerne-fn/capitalise'
import Page from '../components/Page'
import { useLocation } from '../hooks/locations'

type LocationUserAssignment = {
  user: any
  property_ids: number[]
  assigned: boolean
  implicit: boolean
  assignable: boolean
  disabled_reason?: string | null
}

type AssignmentRow = {
  id: number
  full_name: string
  email: string
  role: any
  position?: string
  phone?: string
  property_ids: number[]
  assigned: boolean
  implicit: boolean
  assignable: boolean
  disabled_reason?: string | null
}

const EMPTY_ASSIGNMENTS: LocationUserAssignment[] = []

const Toolbar = () => (
  <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
    <GridToolbarFilterButton />
  </GridToolbarContainer>
)

const AssignPropertyUsers = () => {
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const { id: propertyId } = useParams<{ id: string }>()
  const { data: property, isLoading: isPropertyLoading } = useLocation(propertyId)
  const [selectionModel, setSelectionModel] = useState<any[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    data: assignmentsData,
    isLoading: isAssignmentsLoading,
    isError,
    refetch,
    isRefetching,
  } = useQuery<LocationUserAssignment[]>(
    ['location-user-assignments', propertyId],
    async () => await api.get(`/locations/${propertyId}/users`).then(res => res.data),
    { enabled: !!propertyId, refetchOnWindowFocus: false },
  )
  const assignments = assignmentsData ?? EMPTY_ASSIGNMENTS

  const rows: AssignmentRow[] = useMemo(
    () =>
      assignments.map(assignment => ({
        id: assignment.user.id,
        ...assignment.user,
        property_ids: assignment.property_ids,
        assigned: assignment.assigned,
        implicit: assignment.implicit,
        assignable: assignment.assignable,
        disabled_reason: assignment.disabled_reason,
      })),
    [assignments],
  )

  useEffect(() => {
    setSelectionModel(rows.filter(row => row.assigned && !row.implicit).map(row => row.id))
  }, [rows])

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'full_name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 240,
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 180,
        valueGetter: params => params.row.role.name,
      },
      {
        field: 'portfolio_type',
        headerName: 'Portfolio Type',
        width: 180,
        valueGetter: params => capitalise(params.row.role.attributes.portfolio_type),
        renderCell: params => (
          <Stack direction="row" gap={1} alignItems="center">
            <PortfolioTypeIcon type={params.row.role.attributes.portfolio_type} />
            {capitalise(params.row.role.attributes.portfolio_type)}
          </Stack>
        ),
      },
      {
        field: 'properties',
        headerName: 'Properties',
        width: 130,
        valueGetter: params =>
          params.row.role.attributes.portfolio_type === PortfolioType.Organisation
            ? 'All'
            : params.row.property_ids.length,
      },
      {
        field: 'access',
        headerName: 'Access',
        width: 170,
        sortable: false,
        valueGetter: params => (params.row.assigned ? 'Assigned' : 'Not assigned'),
        renderCell: params => {
          if (params.row.implicit) {
            return <Chip size="small" label="All properties" color="info" variant="outlined" />
          }

          const chip = params.row.assigned ? (
            <Chip size="small" label="Assigned" color="success" variant="outlined" />
          ) : (
            <Chip size="small" label="Not assigned" variant="outlined" />
          )

          return params.row.disabled_reason ? (
            <Tooltip title={params.row.disabled_reason}>{chip}</Tooltip>
          ) : (
            chip
          )
        },
      },
    ],
    [],
  )

  const handleSubmit = async () => {
    setIsSubmitting(true)

    try {
      const res = await api.put(`/locations/${propertyId}/users`, {
        user_ids: selectionModel.map(Number),
      })
      const errors = res.data?.errors ?? []
      const changed = (res.data?.success ?? []).filter(result => result.added || result.removed).length

      if (changed > 0) {
        enqueueSnackbar('User assignments updated', { variant: 'success' })
      } else if (errors.length === 0) {
        enqueueSnackbar('No assignment changes', { variant: 'info' })
      }

      if (errors.length > 0) {
        const hasError = errors.some(error => error.level === 'error')
        enqueueSnackbar(errors.map(error => `${error.user.full_name}: ${error.reason}`).join('; '), {
          variant: hasError ? 'error' : 'warning',
        })
      }

      await refetch()
    } catch (err: any) {
      console.error(err)
      enqueueSnackbar(err?.response?.data?.detail || err?.message || 'Failed to update user assignments', {
        variant: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const isLoading = isPropertyLoading || isAssignmentsLoading
  const propertyName = property?.name ?? 'Property'

  return (
    <Page
      title={`Assign users: ${propertyName}`}
      back
      size="xl"
      actions={
        <LoadingButton onClick={handleSubmit} variant="contained" loading={isSubmitting} disabled={isLoading}>
          Save
        </LoadingButton>
      }>
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load user assignments
        </Alert>
      )}

      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <Box height={760} mb={6}>
          <DataGrid
            components={{ Toolbar }}
            sx={{ backgroundColor: theme.palette.background.default }}
            density="compact"
            rows={rows}
            columns={columns}
            checkboxSelection
            disableSelectionOnClick
            loading={isRefetching}
            selectionModel={selectionModel}
            onSelectionModelChange={ids => setSelectionModel(ids)}
            isRowSelectable={params => params.row.assignable}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {selectionModel.length} explicitly assigned
          </Typography>
        </Box>
      )}
    </Page>
  )
}

export default AssignPropertyUsers
